import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Tooltip,
  Grid,
  Divider,
  debounce,
} from "@mui/material";
import { GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import SearchOutlined from "@ant-design/icons/SearchOutlined";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import EmptyTable from "components/third-party/EmptyTable";
import { formatDateTimeIST } from "utils/date";
import {
  ICurrentlyInsideGridFilter,
  ICurrentlyInsideItem,
  IEntryExitDashboardFilter,
} from "../api/types";
import { useGetCurrentlyInside } from "../api/hooks";
import { PostModel } from "common/models/BaseModel";

interface CurrentlyInsideTableProps {
  filter: IEntryExitDashboardFilter;
}

export default function CurrentlyInsideTable({
  filter,
}: CurrentlyInsideTableProps) {
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "entryTime", sort: "desc" },
  ]);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val: string) => {
        setSearchText(val);
      }, 400),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchText(val);
  };

  // Reset to first page when search text or filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    filter.memberTypeID,
    filter.instituteID,
    filter.departmentID,
    filter.programID,
    searchText,
  ]);

  const postModel = useMemo<PostModel<ICurrentlyInsideGridFilter>>(() => {
    return {
      filterModel: {
        memberTypeID: filter.memberTypeID,
        instituteID: filter.instituteID,
        departmentID: filter.departmentID,
        programID: filter.programID,
        searchText: searchText || undefined,
      },
      pagingModel: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      sortModel: sortModel,
    };
  }, [filter, searchText, paginationModel, sortModel]);

  const { data, isLoading, isFetching, refetch } = useGetCurrentlyInside(postModel);

  const rowCountRef = useRef(data?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (data?.apiData?.count !== undefined) {
      rowCountRef.current = data?.apiData?.count;
    }
    return rowCountRef.current;
  }, [data?.apiData?.count]);

  const handlePagination = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const handleSorting = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  const formatDuration = (mins: number) => {
    if (!mins || mins <= 0) return "< 1 min";
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hours > 0) return `${hours}h ${rem}m`;
    return `${rem} min${rem > 1 ? "s" : ""}`;
  };

  const getDurationColor = (mins: number): "success" | "warning" | "error" | "default" => {
    if (mins > 240) return "error"; // > 4 hours
    if (mins > 120) return "warning"; // > 2 hours
    return "success";
  };

  const columns: GridColDef<ICurrentlyInsideItem>[] = useMemo(
    () => [
      {
        field: "memberName",
        headerName: "Member Name",
        flex: 1.4,
        minWidth: 160,
        renderCell: (params) => (
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "memberTypeName",
        headerName: "Member Type",
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => <>{params.value || "-"}</>,
      },
      {
        field: "memberCode",
        headerName: "Member Code",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main" }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "biometricCode",
        headerName: "Biometric Code",
        flex: 0.9,
        minWidth: 130,
        renderCell: (params) => <>{params.value || "-"}</>,
      },
      {
        field: "departmentName",
        headerName: "Department / Institute",
        flex: 1.4,
        minWidth: 170,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2">
              {params.value || "-"}{params.row.instituteName ? ` - ${params.row.instituteName}` : ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "programName",
        headerName: "Program",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => <>{params.value || "-"}</>,
      },
      {
        field: "entryTime",
        headerName: "Entry Time",
        flex: 1.2,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body1">
            {params.value ? formatDateTimeIST(params.value) : "-"}
          </Typography>
        ),
      },
      {
        field: "minutesInside",
        headerName: "Time Inside",
        flex: 1,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={`${params.value} total minutes`}>
            <Chip
              icon={<ClockCircleOutlined style={{ fontSize: 13 }} />}
              label={formatDuration(params.value)}
              size="small"
              color={getDurationColor(params.value)}
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: 10 }}
            />
          </Tooltip>
        ),
      },
      {
        field: "remarks",
        headerName: "Source / Mode",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => {
          const isBiometric = params.value?.toLowerCase().includes("punching machine");
          return (
            <Chip
              label={isBiometric ? "Biometric Punch" : (params.value || "Manual Entry")}
              size="small"
              color={isBiometric ? "info" : "default"}
              variant={isBiometric ? "filled" : "outlined"}
              sx={{ borderRadius: 10, fontSize: "0.75rem" }}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <Card elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component={"h3"}
            display="flex"
            alignItems="center"
            gap={1}
          >
            Currently Inside Library
            <Chip
              label={`${rowCount} Inside`}
              color="success"
              size="small"
              sx={{ fontWeight: 700, borderRadius: 10 }}
            />
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
          <TextField
            size="medium"
            placeholder="Search member, code..."
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 240 }, flexGrow: { xs: 1, sm: 0 } }}
          />
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container sx={{ height: { xs: 380, sm: 440, md: 480 }, width: "100%" }}>
        <StyledDataGrid
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "wrap",
              fontWeight: 600,
              lineHeight: "normal",
              fontSize: "13px",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "wrap",
              alignContent: "center",
              fontSize: 14,
            },
          }}
          density="compact"
          disableRowSelectionOnClick
          getRowId={(row) => row.entryExitID}
          columns={columns}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePagination}
          sortModel={sortModel}
          onSortModelChange={handleSorting}
          getRowHeight={() => "auto"}
          paginationMode="server"
          sortingMode="server"
          rows={data?.apiData?.data || []}
          rowCount={rowCount}
          disableColumnFilter
          loading={isLoading || isFetching}
          slots={{
            noRowsOverlay: () => (
              <EmptyTable
                title="No Patrons Currently Inside"
                description="All checked-in visitors have logged their exits, or no entry records are active."
              />
            ),
          }}
        />
      </Grid>
    </Card>
  );
}
